<script setup lang="ts">
import { computed, type StyleValue } from 'vue';

export interface NineSliceFrameProps {
  /** Path to the image (relative to public folder) */
  imagePath: string;
  /** Slice values - number for uniform, object for per-edge */
  slice?: number | { top: number; right: number; bottom: number; left: number };
  /** Visual border width in pixels */
  borderWidth?: number;
  /** Border repeat mode */
  repeat?: 'stretch' | 'repeat' | 'round' | 'space';
  /** Use 'fill' to show center of image as background */
  fill?: boolean;
  /** Use pixelated rendering for pixel art */
  pixelated?: boolean;
  /** Additional CSS class names */
  class?: string;
  /** Additional inline styles */
  style?: StyleValue;
}

const props = withDefaults(defineProps<NineSliceFrameProps>(), {
  slice: 8,
  borderWidth: 5,
  repeat: 'stretch',
  fill: true,
  pixelated: true,
  class: '',
  style: () => ({}),
});

const sliceValue = computed(() => {
  return typeof props.slice === 'number'
    ? props.slice.toString()
    : `${props.slice.top} ${props.slice.right} ${props.slice.bottom} ${props.slice.left}`;
});

const borderImageValue = computed(() => {
  return `url("${props.imagePath}") ${sliceValue.value} ${props.fill ? 'fill' : ''} / ${props.borderWidth}px / 0px ${props.repeat}`;
});

const frameStyle = computed((): StyleValue => {
  const base = {
    borderWidth: '1px',
    borderStyle: 'solid',
    borderImage: borderImageValue.value,
    imageRendering: props.pixelated ? ('pixelated' as const) : ('auto' as const),
  };

  if (typeof props.style === 'object' && !Array.isArray(props.style)) {
    return { ...base, ...props.style };
  }

  return base;
});
</script>

<template>
  <div :class="props.class" :style="frameStyle">
    <slot />
  </div>
</template>
